import { ref, computed } from 'vue';

import { defineStore } from 'pinia';

import { steps, selectionTypes, errorTypes, errorMessages } from '../utils/job-title-recommender';

import { saveJobTitle, getJobTitleRecommendations } from '../api/profile';
import { setAuthState } from '../composables/auth';

const { getUser, setUser } = setAuthState();

export const useJobTitleRecommenderStore = defineStore('job-title-recommender', () => {
	// User snapshot
	const user = getUser();

	// WebSocket listener state
	let isWebSocketSetup = false;

	// State
	const currentStep = ref(steps.CHOOSE_TARGET_JOB);
	const isRoleConfirmed = ref(false);
	const isTitleEvaluationLoading = ref(false);
	const isJobSavingLoading = ref(false);
	const selectedRole = ref('');
	const selectedOption = ref(null);
	const isRecommendedTitlesLoading = ref(false);
	const confirmedRole = ref(user?.job_title);
	const roleInput = ref(confirmedRole.value || '');
	const jobTitleReasoningObject = ref(user?.job_title_reasoning || {});
	const errors = ref({});
	const selectedType = ref(selectionTypes.INPUT);
	const recommendedTitles = ref([]);
	const isParsingCompleted = ref(false);
	const isPrimaryResumePresent = ref(false);

	// Computed
	const isStrongMatch = computed(() => {
		return jobTitleReasoningObject.value.fit_assessment === 'strong_match';
	});
	const isConfirmButtonEnabled = computed(() => {
		return (selectedOption.value !== null || roleInput.value !== '') && !isJobSavingLoading.value;
	});
	const isTitleEvaluated = computed(() => {
		return Object.keys(jobTitleReasoningObject.value).length > 0 && roleInput.value !== '';
	});

	// Actions
	const confirmRoleHandler = () => {
		isRoleConfirmed.value = true;
	};

	const setSelectedType = (type) => {
		selectedType.value = type;
	};

	const setCurrentStep = (step) => {
		currentStep.value = step;
	};

	const clearSelectedOptions = () => {
		selectedOption.value = null;
	};

	const setRoleInputToConfirmedRole = () => {
		roleInput.value = confirmedRole.value;
	};

	const setRecommendedTitles = (titles) => {
		recommendedTitles.value = titles;
	};

	const updateRecommendedTitlesData = (data) => {
		if (!data) {
			errors.value[errorTypes.SOMETHING_WENT_WRONG] = errorMessages[errorTypes.SOMETHING_WENT_WRONG];
			return;
		}

		const { titles = [], is_parsing_completed = false, primary_resume_present = false } = data || {};
		isParsingCompleted.value = is_parsing_completed;
		isPrimaryResumePresent.value = primary_resume_present;
		setRecommendedTitles(titles);
	};

	const confirmSelectionHandler = async () => {
		try {
			isTitleEvaluationLoading.value = true;
			isJobSavingLoading.value = true;

			if (selectedType.value === selectionTypes.INPUT) {
				selectedRole.value = roleInput.value;
			} else if (selectedType.value === selectionTypes.OPTION) {
				selectedRole.value = selectedOption.value;
				isRoleConfirmed.value = true;
			}

			confirmedRole.value = selectedRole.value;
			setCurrentStep(steps.CONFIRM_SELECTION);
			await saveJobTitleHandler();
		} finally {
			isJobSavingLoading.value = false;
			selectedOption.value = null;
		}
	};

	const saveJobTitleHandler = async () => {
		try {
			delete errors.value[errorTypes.FAIL_EVALUATING_TITLE];

			await saveJobTitle(selectedRole.value);
		} catch {
			isTitleEvaluationLoading.value = false;
			errors.value[errorTypes.FAIL_EVALUATING_TITLE] = errorMessages[errorTypes.FAIL_EVALUATING_TITLE];
		}
	};

	const getRecommendedTitles = async () => {
		try {
			delete errors.value[errorTypes.FAIL_FETCHING_RECOMMENDED_TITLES];
			delete errors.value[errorTypes.FAIL_MISSING_RECOMMENDED_TITLES];

			isRecommendedTitlesLoading.value = true;

			const data = await getJobTitleRecommendations();

			updateRecommendedTitlesData(data);

			if (recommendedTitles.value.length === 0 && isParsingCompleted.value && isPrimaryResumePresent.value) {
				errors.value[errorTypes.FAIL_MISSING_RECOMMENDED_TITLES] =
                    errorMessages[errorTypes.FAIL_MISSING_RECOMMENDED_TITLES];
			}
		} catch {
			errors.value[errorTypes.FAIL_FETCHING_RECOMMENDED_TITLES] =
                errorMessages[errorTypes.FAIL_FETCHING_RECOMMENDED_TITLES];
		} finally {
			isRecommendedTitlesLoading.value = false;
		}
	};

	const refreshState = () => {
		const freshUser = getUser();

		currentStep.value = steps.CHOOSE_TARGET_JOB;
		isRoleConfirmed.value = false;
		isTitleEvaluationLoading.value = false;
		isJobSavingLoading.value = false;
		selectedRole.value = '';
		selectedOption.value = null;
		isRecommendedTitlesLoading.value = false;
		confirmedRole.value = freshUser?.job_title;
		roleInput.value = confirmedRole.value || '';
		jobTitleReasoningObject.value = freshUser?.job_title_reasoning || {};
		errors.value = {};
		selectedType.value = selectionTypes.INPUT;
		recommendedTitles.value = [];
		isParsingCompleted.value = false;
		isPrimaryResumePresent.value = false;
	};

	const setupWebSocketListeners = () => {
		if (isWebSocketSetup) {return;}

		const currentUser = getUser();
		if (!currentUser) {return;}

		window.Echo.private(`recommended-titles.${currentUser.id}`).listen('RecommendedTitlesEvent', (event) => {
			updateRecommendedTitlesData(event.data);
		});

		window.Echo.private(`job-title-evaluation.${currentUser.id}`).listen('JobTitleEvaluationEvent', (event) => {
			if (event.data) {
				setUser({ ...getUser(), job_title_reasoning: event.data });
				jobTitleReasoningObject.value = event.data;
			}
			isTitleEvaluationLoading.value = false;
		});

		isWebSocketSetup = true;
	};

	const teardownWebSocketListeners = () => {
		const currentUser = getUser();
		if (!currentUser || !isWebSocketSetup) {return;}

		window.Echo.leave(`recommended-titles.${currentUser.id}`);
		window.Echo.leave(`job-title-evaluation.${currentUser.id}`);
		isWebSocketSetup = false;
	};

	return {
		// State
		isRoleConfirmed,
		selectedType,
		currentStep,
		selectedRole,
		isTitleEvaluationLoading,
		jobTitleReasoningObject,
		isTitleEvaluated,
		isStrongMatch,
		confirmedRole,
		selectedOption,
		roleInput,
		isConfirmButtonEnabled,
		errors,
		isParsingCompleted,
		isRecommendedTitlesLoading,
		recommendedTitles,

		// Actions
		confirmRoleHandler,
		setSelectedType,
		setCurrentStep,
		saveJobTitleHandler,
		getRecommendedTitles,
		confirmSelectionHandler,
		setRoleInputToConfirmedRole,
		clearSelectedOptions,
		setRecommendedTitles,
		updateRecommendedTitlesData,
		refreshState,
		setupWebSocketListeners,
		teardownWebSocketListeners,
	};
});
